import { applyEffects, clearEffects, ensureHostWrapper, unwrapHost } from './effects'
import { ICONS } from './icons'
import { trapFocus, releaseFocus, injectSkipLink, removeSkipLink } from './keyboard'
import { renderPanel } from './render'
import { buildStyles, DEFAULT_VARS, STYLE_ID } from './styles'
import {
  DEFAULT_STATE,
  STORAGE_KEY,
  type AccessibilityProfile,
  type AccessifyConfig,
  type AccessifyState,
  type ColorScheme,
  type Lang,
  type TextAlignment,
  type TriggerScheme,
  type WidgetSize,
} from './types'
import { runWcagScan, type WcagResult } from './wcag'

export { SUPPORTED_LANGS } from './types'
export type {
  AccessibilityProfile,
  AccessifyConfig,
  AccessifyState,
  ColorScheme,
  Lang,
  Position,
  SupportedLang,
  TextAlignment,
  TriggerScheme,
  WidgetSize,
} from './types'

const PROFILE_PRESETS: Record<AccessibilityProfile, Partial<AccessifyState>> = {
  'seizure-safe':         { stopAnimations: true, lowSaturation: true },
  'vision-impaired':      { fontSize: 2, contentScale: 1, lineHeight: 1 },
  'adhd-friendly':        { highlightLinks: true, highlightTitles: true, readableFont: true },
  'cognitive-disability': { highlightLinks: true, highlightTitles: true, readableFont: true, lineHeight: 2 },
  'keyboard-navigation':  {},
  'screen-reader':        {},
  'color-blind':          { colorBlind: true },
  'dyslexia':             { readableFont: true, letterSpacing: 2, lineHeight: 2 },
}

const STEPPER_BOUNDS: Record<string, [number, number]> = {
  contentScale: [-5, 5],
  fontSize: [-5, 10],
  lineHeight: [-2, 6],
  letterSpacing: [-2, 6],
}

const COLOR_EXCLUSIVE: Array<keyof AccessifyState> = [
  'darkContrast',
  'lightContrast',
  'highContrast',
  'monochrome',
  'invertColors',
  'highSaturation',
  'lowSaturation',
]

export class Accessify {
  private static instance: Accessify | null = null

  /**
   * Initializes and mounts a global Accessify widget singleton.
   * If an instance is already mounted, it is destroyed before mounting the new one.
   */
  static init(config?: AccessifyConfig): Accessify {
    if (Accessify.instance) {
      Accessify.instance.destroy()
    }
    const widget = new Accessify(config)
    widget.mount()
    Accessify.instance = widget
    if (typeof window !== 'undefined') {
      ;(window as any).accessify = widget
    }
    return widget
  }

  /**
   * Returns the currently active global Accessify instance, if any.
   */
  static getInstance(): Accessify | null {
    return Accessify.instance
  }

  private readonly config: AccessifyConfig
  private size: WidgetSize
  private lang: Lang
  private scheme: ColorScheme
  private state: AccessifyState
  private root: HTMLDivElement | null = null
  private trigger: HTMLButtonElement | null = null
  private overlay: HTMLDivElement | null = null
  private panel: HTMLDivElement | null = null
  private isOpen = false
  private isStatementOpen = false
  private wcagResult: WcagResult | null = null
  private wcagScanning = false

  constructor(config: AccessifyConfig = {}) {
    this.config = {
      position: 'bottom-right',
      persistence: true,
      lang: 'en',
      colorScheme: 'light',
      ...config,
    }
    this.size = this.config.size ?? 'M'
    this.lang = this.config.lang ?? 'en'
    this.scheme = this.config.colorScheme ?? 'light'
    this.state = this.loadState()
  }

  mount(_target?: HTMLElement): void {
    if (typeof document === 'undefined') return
    this.injectStyles()

    ensureHostWrapper()
    if (this.state.profile === 'keyboard-navigation' || this.state.profile === 'screen-reader') {
      injectSkipLink()
    }

    this.root = document.createElement('div')
    this.root.className = 'accessify-root'
    this.root.setAttribute('role', 'complementary')
    this.root.setAttribute('aria-label', 'Accessibility Widget')
    this.root.dataset.scheme = this.scheme === 'dark' ? 'dark' : 'light'
    this.applyTheme()

    this.trigger = document.createElement('button')
    this.trigger.className = 'accessify-trigger'
    this.trigger.type = 'button'
    this.trigger.dataset.position = this.config.position!
    this.trigger.setAttribute('aria-label', 'Open accessibility menu')
    this.trigger.setAttribute('aria-expanded', 'false')
    this.trigger.innerHTML = this.config.triggerIcon ?? ICONS.wheelchair
    this.trigger.addEventListener('click', () => this.toggle())

    this.overlay = document.createElement('div')
    this.overlay.className = 'accessify-overlay'
    this.overlay.addEventListener('click', () => this.close())

    this.panel = document.createElement('div')
    this.panel.className = 'accessify-panel'
    this.panel.setAttribute('role', 'dialog')
    this.panel.setAttribute('aria-modal', 'true')
    this.panel.setAttribute('aria-label', 'Accessibility settings')
    this.panel.dataset.position = this.config.position!
    this.panel.dataset.size = this.size
    this.panel.addEventListener('click', e => this.handlePanelClick(e))

    this.root.append(this.trigger, this.overlay, this.panel)
    // Always append to <body> directly so the widget lives as a sibling
    // of #accessify-host and is never affected by the effects (font-size,
    // contrast, filters etc) applied to the page content wrapper.
    document.body.appendChild(this.root)

    this.update()
    applyEffects(this.state)
  }

  destroy(): void {
    clearEffects()
    unwrapHost()
    releaseFocus()
    removeSkipLink()
    if (this.root) {
      this.root.remove()
      this.root = null
    }
    this.trigger = null
    this.overlay = null
    this.panel = null
    if (Accessify.instance === this) {
      Accessify.instance = null
      if (typeof window !== 'undefined' && (window as any).accessify === this) {
        delete (window as any).accessify
      }
    }
  }

  open(): void {
    this.isOpen = true
    this.trigger?.setAttribute('aria-expanded', 'true')
    this.config.onOpen?.()
    this.update()
    if (this.panel && this.trigger) trapFocus(this.panel, this.trigger)
  }

  close(): void {
    this.isOpen = false
    this.isStatementOpen = false
    this.trigger?.setAttribute('aria-expanded', 'false')
    this.config.onClose?.()
    releaseFocus()
    this.update()
  }

  openStatement(): void {
    this.isStatementOpen = true
    this.update()
    const closeBtn = this.panel?.querySelector<HTMLButtonElement>('[data-action="close-statement"]')
    closeBtn?.focus()
  }

  closeStatement(): void {
    this.isStatementOpen = false
    this.update()
    const openBtn = this.panel?.querySelector<HTMLButtonElement>('[data-action="open-statement"]')
    openBtn?.focus()
  }

  getIsStatementOpen(): boolean {
    return this.isStatementOpen
  }

  toggle(): void {
    if (this.isOpen) this.close()
    else this.open()
  }

  reset(): void {
    this.state = { ...DEFAULT_STATE }
    this.saveState()
    applyEffects(this.state)
    this.config.onReset?.()
    this.update()
  }

  getState(): AccessifyState {
    return { ...this.state }
  }

  getIsOpen(): boolean {
    return this.isOpen
  }

  setSize(size: WidgetSize): void {
    this.size = size
    if (this.panel) this.panel.dataset.size = size
    this.config.onSizeChange?.(size)
    this.update()
  }

  setLang(lang: Lang): void {
    this.lang = lang
    this.config.onLangChange?.(lang)
    this.update()
  }

  getLang(): Lang {
    return this.lang
  }

  getColorScheme(): ColorScheme {
    return this.scheme
  }

  setColorScheme(scheme: ColorScheme): void {
    this.scheme = scheme
    this.applyScheme()
    // If trigger is on 'auto' (matches colorScheme), refresh it too so the
    // trigger color follows when colorScheme changes at runtime.
    if ((this.config.triggerScheme ?? 'auto') === 'auto') this.applyTriggerScheme()
    this.config.onColorSchemeChange?.(scheme)
    this.update()
  }

  private applyScheme(): void {
    if (!this.root) return
    this.root.dataset.scheme = this.scheme === 'dark' ? 'dark' : 'light'
  }

  private applyTheme(): void {
    if (!this.root) return
    const t = this.config.theme
    if (t) {
      if (t.primary) this.root.style.setProperty('--acc-primary', t.primary)
      if (t.background) this.root.style.setProperty('--acc-bg', t.background)
      if (t.text) this.root.style.setProperty('--acc-text', t.text)
      if (t.headerBackground) this.root.style.setProperty('--acc-header-bg', t.headerBackground)
    }
    this.applyTriggerScheme()
  }

  private applyTriggerScheme(): void {
    if (!this.root) return
    // Resolution chain:
    //   1. explicit config.triggerScheme other than 'auto' wins
    //   2. 'auto' (or unset) → match this.scheme (the resolved colorScheme,
    //      which itself defaults to 'light' when not passed)
    const wanted = this.config.triggerScheme ?? 'auto'
    if (wanted === 'blue') {
      this.root.style.setProperty('--acc-trigger-bg', '#0066ff')
      this.root.style.setProperty('--acc-trigger-icon', '#ffffff')
      return
    }
    let resolved: 'dark' | 'light'
    if (wanted === 'dark' || wanted === 'light') {
      resolved = wanted
    } else {
      resolved = this.scheme === 'dark' ? 'dark' : 'light'
    }
    if (resolved === 'dark') {
      this.root.style.setProperty('--acc-trigger-bg', '#0c0c0c')
      this.root.style.setProperty('--acc-trigger-icon', '#ffffff')
    } else {
      this.root.style.setProperty('--acc-trigger-bg', '#ffffff')
      this.root.style.setProperty('--acc-trigger-icon', '#0c0c0c')
    }
  }

  setTriggerScheme(scheme: TriggerScheme): void {
    this.config.triggerScheme = scheme
    this.applyTriggerScheme()
  }

  setTriggerIcon(icon: string): void {
    this.config.triggerIcon = icon
    if (this.trigger) {
      this.trigger.innerHTML = icon
    }
  }

  setBrandTitle(title: string): void {
    this.config.brandTitle = title
    this.update()
  }

  updateConfig(config: Partial<AccessifyConfig>): void {
    Object.assign(this.config, config)
    if (config.size !== undefined && config.size !== this.size) {
      this.setSize(config.size)
    }
    if (config.lang !== undefined && config.lang !== this.lang) {
      this.setLang(config.lang)
    }
    if (config.colorScheme !== undefined && config.colorScheme !== this.scheme) {
      this.setColorScheme(config.colorScheme)
    }
    if (config.triggerScheme !== undefined) {
      this.setTriggerScheme(config.triggerScheme)
    }
    if (config.triggerIcon !== undefined) {
      this.setTriggerIcon(config.triggerIcon)
    }
    if (config.brandTitle !== undefined) {
      this.setBrandTitle(config.brandTitle)
    }
  }

  private handlePanelClick(e: MouseEvent): void {
    const target = e.target as HTMLElement
    if (target.closest<HTMLElement>('.accessify-close')) { this.close(); return }
    if (this.handleLangClick(target)) return
    if (this.handleActionClick(target)) return
    if (this.handleSizeClick(target)) return
    if (this.handleSchemeClick(target)) return
    if (this.handleProfileClick(target)) return
    if (this.handleStepClick(target)) return
    if (this.handleToggleClick(target)) return
    if (this.handleAlignClick(target)) return
    if (this.handleSwatchClick(target)) return
    if (this.handleCancelColorClick(target)) return
    this.handleColorClick(target)
  }

  private handleLangClick(target: HTMLElement): boolean {
    const toggleBtn = target.closest<HTMLElement>('[data-action="toggle-lang-menu"]')
    if (toggleBtn) {
      const picker = toggleBtn.closest<HTMLElement>('.accessify-lang-picker')
      if (picker) {
        const isOpen = picker.classList.toggle('open')
        toggleBtn.setAttribute('aria-expanded', String(isOpen))
      }
      return true
    }

    const item = target.closest<HTMLElement>('[data-lang]')
    if (item) {
      const lang = item.dataset.lang as Lang
      const picker = item.closest<HTMLElement>('.accessify-lang-picker')
      if (picker) {
        picker.classList.remove('open')
        picker.querySelector('[data-action="toggle-lang-menu"]')?.setAttribute('aria-expanded', 'false')
      }
      this.setLang(lang)
      return true
    }

    const openPicker = this.panel?.querySelector<HTMLElement>('.accessify-lang-picker.open')
    if (openPicker && !openPicker.contains(target)) {
      openPicker.classList.remove('open')
      openPicker.querySelector('[data-action="toggle-lang-menu"]')?.setAttribute('aria-expanded', 'false')
    }

    return false
  }

  private handleActionClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-action]')
    if (!btn) return false
    if (btn.dataset.action === 'reset') this.reset()
    else if (btn.dataset.action === 'analyze') this.runAnalysis()
    else if (btn.dataset.action === 'open-statement') this.openStatement()
    else if (btn.dataset.action === 'close-statement') this.closeStatement()
    return true
  }

  private handleSizeClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-size]')
    if (!btn || !this.panel?.contains(btn) || !btn.parentElement?.classList.contains('accessify-size-toggle')) return false
    this.setSize(btn.dataset.size as WidgetSize)
    return true
  }

  private handleSchemeClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-scheme-option]')
    if (!btn || !this.panel?.contains(btn) || !btn.closest('.accessify-theme-toggle')) return false
    this.setColorScheme(btn.dataset.schemeOption as ColorScheme)
    return true
  }

  private handleProfileClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-profile]')
    if (!btn) return false
    this.toggleProfile(btn.dataset.profile as AccessibilityProfile)
    return true
  }

  private handleStepClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-step]')
    if (!btn) return false
    const tile = btn.closest<HTMLElement>('[data-stepper]')
    if (tile) this.adjustStepper(tile.dataset.stepper as keyof AccessifyState, Number.parseInt(btn.dataset.step!, 10))
    return true
  }

  private handleToggleClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-toggle]')
    if (!btn) return false
    this.toggleFlag(btn.dataset.toggle as keyof AccessifyState)
    return true
  }

  private handleAlignClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-align]')
    if (!btn) return false
    const a = btn.dataset.align as TextAlignment
    this.state.textAlignment = this.state.textAlignment === a ? 'default' : a
    this.commit()
    return true
  }

  private handleColorClick(target: HTMLElement): void {
    const btn = target.closest<HTMLElement>('[data-color]')
    if (btn) this.toggleColor(btn.dataset.color as keyof AccessifyState)
  }

  private handleSwatchClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-color-target][data-color-value]')
    if (!btn) return false
    const colorTarget = btn.dataset.colorTarget as 'text' | 'title' | 'background'
    const colorValue = btn.dataset.colorValue!
    if (colorTarget === 'text') {
      this.state.textColor = this.state.textColor === colorValue ? null : colorValue
    } else if (colorTarget === 'title') {
      this.state.titleColor = this.state.titleColor === colorValue ? null : colorValue
    } else if (colorTarget === 'background') {
      this.state.backgroundColor = this.state.backgroundColor === colorValue ? null : colorValue
    }
    this.commit()
    return true
  }

  private handleCancelColorClick(target: HTMLElement): boolean {
    const btn = target.closest<HTMLElement>('[data-cancel-color]')
    if (!btn) return false
    const colorTarget = btn.dataset.cancelColor as 'text' | 'title' | 'background'
    if (colorTarget === 'text') this.state.textColor = null
    else if (colorTarget === 'title') this.state.titleColor = null
    else if (colorTarget === 'background') this.state.backgroundColor = null
    this.commit()
    return true
  }

  setTextColor(color: string | null): void {
    this.state.textColor = color
    this.commit()
  }

  setTitleColor(color: string | null): void {
    this.state.titleColor = color
    this.commit()
  }

  setBackgroundColor(color: string | null): void {
    this.state.backgroundColor = color
    this.commit()
  }

  private async runAnalysis(): Promise<void> {
    this.wcagScanning = true
    this.update()
    // Yield to allow re-render before scanning
    await new Promise(r => setTimeout(r, 30))
    const host = document.getElementById('accessify-host') ?? document.body
    this.wcagResult = runWcagScan(host)
    this.wcagScanning = false
    this.update()
  }

  private toggleProfile(id: AccessibilityProfile): void {
    if (this.state.profile === id) {
      this.state = { ...DEFAULT_STATE }
    } else {
      const preset = PROFILE_PRESETS[id] ?? {}
      this.state = { ...DEFAULT_STATE, profile: id, ...preset }
    }
    if (this.state.profile === 'keyboard-navigation' || this.state.profile === 'screen-reader') {
      injectSkipLink()
    } else {
      removeSkipLink()
    }
    this.commit()
  }

  private adjustStepper(key: keyof AccessifyState, delta: number): void {
    const bounds = STEPPER_BOUNDS[key as string]
    if (!bounds) return
    const current = this.state[key] as number
    const next = Math.max(bounds[0], Math.min(bounds[1], current + delta))
    ;(this.state as unknown as Record<string, unknown>)[key] = next
    this.commit()
  }

  private toggleFlag(key: keyof AccessifyState): void {
    const v = this.state[key]
    if (typeof v !== 'boolean') return
    const next = !v
    if (next && (key === 'bigBlackCursor' || key === 'bigWhiteCursor')) {
      if (key === 'bigBlackCursor') this.state.bigWhiteCursor = false
      if (key === 'bigWhiteCursor') this.state.bigBlackCursor = false
    }
    ;(this.state as unknown as Record<string, unknown>)[key] = next
    this.commit()
  }

  private toggleColor(key: keyof AccessifyState): void {
    const current = this.state[key]
    if (typeof current !== 'boolean') return
    const next = !current
    if (next && COLOR_EXCLUSIVE.includes(key)) {
      for (const k of COLOR_EXCLUSIVE) {
        if (k !== key) (this.state as unknown as Record<string, unknown>)[k] = false
      }
    }
    ;(this.state as unknown as Record<string, unknown>)[key] = next
    this.commit()
  }

  private commit(): void {
    this.saveState()
    applyEffects(this.state)
    this.update()
  }

  private getElementFocusSelector(el: Element): string | null {
    if (!this.panel || !this.panel.contains(el)) return null
    if (!(el instanceof HTMLElement)) return null

    if (el.dataset.toggle) return `[data-toggle="${el.dataset.toggle}"]`
    if (el.dataset.profile) return `[data-profile="${el.dataset.profile}"]`
    if (el.dataset.size) return `.accessify-size-toggle [data-size="${el.dataset.size}"]`
    if (el.dataset.schemeOption) return `.accessify-theme-toggle [data-scheme-option="${el.dataset.schemeOption}"]`
    if (el.dataset.step) {
      const tile = el.closest<HTMLElement>('[data-stepper]')
      if (tile?.dataset.stepper) {
        return `[data-stepper="${tile.dataset.stepper}"] [data-step="${el.dataset.step}"]`
      }
    }
    if (el.dataset.color) return `[data-color="${el.dataset.color}"]`
    if (el.dataset.align) return `[data-align="${el.dataset.align}"]`
    if (el.dataset.action) return `[data-action="${el.dataset.action}"]`
    if (el.dataset.lang) return `[data-lang="${el.dataset.lang}"]`
    if (el.dataset.colorValue && el.dataset.colorTarget) {
      return `[data-color-target="${el.dataset.colorTarget}"][data-color-value="${el.dataset.colorValue}"]`
    }
    if (el.classList.contains('accessify-close')) return '.accessify-close'
    if (el.classList.contains('accessify-lang-btn')) return '.accessify-lang-btn'
    if (el.id) return `#${el.id}`

    return null
  }

  private update(): void {
    if (!this.panel) return
    this.panel.classList.toggle('open', this.isOpen)
    this.overlay?.classList.toggle('open', this.isOpen)
    const prevScroll = this.panel.querySelector<HTMLElement>('.accessify-body')?.scrollTop ?? 0

    // Capture focus selector if element is active inside panel
    const focusedSelector = document.activeElement
      ? this.getElementFocusSelector(document.activeElement)
      : null

    this.panel.innerHTML = renderPanel(
      this.state,
      this.size,
      this.lang,
      this.wcagResult,
      this.wcagScanning,
      this.config.brandTitle,
      this.isStatementOpen,
      this.scheme,
    )
    const nextBody = this.panel.querySelector<HTMLElement>('.accessify-body')
    if (nextBody) nextBody.scrollTop = prevScroll
    this.applyScheme()

    // Restore focus if element was focused
    if (focusedSelector) {
      const nextEl = this.panel.querySelector<HTMLElement>(focusedSelector)
      nextEl?.focus()
    }
  }

  private injectStyles(): void {
    if (typeof document === 'undefined') return
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = buildStyles(DEFAULT_VARS)
    document.head.appendChild(style)
  }

  private loadState(): AccessifyState {
    if (!this.config.persistence) return { ...DEFAULT_STATE }
    if (typeof localStorage === 'undefined') return { ...DEFAULT_STATE }
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) return { ...DEFAULT_STATE, ...JSON.parse(stored) }
    } catch {
      // ignore
    }
    return { ...DEFAULT_STATE }
  }

  private saveState(): void {
    if (!this.config.persistence) return
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state))
    } catch {
      // ignore
    }
  }
}
